import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import moment from 'moment';
import * as turf from '@turf/turf';
import { IncidentMapEntity } from './incident-map-entity.service';
import { SafeZoneMapEntity } from './safe-zone-map-entity.service';
import { DataContextService } from './data-context.service';
import { CameraService } from './camera.service';
import { CoresService } from './cores.service';
import { CoreService } from './core.service';
import { GlobalSettingsService } from './global-settings.service';
import { ConfigurationManagerService } from './configuration-manager.service';
import { UpdateMapEntity } from './update-map-entity.service';
import { WindowService } from './window.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TimelineService } from './timeline.service';

@Injectable({ providedIn: 'root' })
export class ArcAngelSharedService {
    constructor(
        private http: HttpClient,
        private dataContextService: DataContextService,
        private cameraService :CameraService,
        private coreService : CoreService,
        private cores: CoresService,
        private globalSettings: GlobalSettingsService,
        private windowService: WindowService,
        private modalService: NgbModal,
        private timelineService: TimelineService
      ) {}

 
  parseIncident(incident: any, eventId: string, playback: boolean): any {
    if (!incident.highGeometries || incident.highGeometries.length === 0) {
      return null;
    }
    const incidentEntity = new IncidentMapEntity(incident, this.dataContextService,this.cameraService,this.coreService,this.globalSettings,this.windowService,this.modalService,this.timelineService,playback);

    if (incident.affectedPerimeters?.length > 0) {
      incidentEntity.setAffectedPerimeterIds(incident.affectedPerimeters);
    }
    try {

      incident.highGeometries?.forEach((geo: any) => {
        incidentEntity.addHighGeometry(
          this.reverseCoordinates(geo.coordinates[0])
        );
      });

      // MEDIUM geometries
      incident.mediumGeometries?.forEach((geo: any) => {
        incidentEntity.addMedGeometry(
          this.reverseCoordinates(geo.coordinates[0])
        );
      });

      // LOW geometries
      incident.lowGeometries?.forEach((geo: any) => {
        incidentEntity.addLowGeometry(
          this.reverseCoordinates(geo.coordinates[0])
        );
      });

    } catch (e) {
      console.error('Error in parseIncident geometry:', e);
    }
    if (incident.focalPoint) {
      incidentEntity.setFocalPoint(incident.focalPoint);
    }
    return incidentEntity;
  }
  updateIncident(incident: any, incidentEntity: any): any {
    incidentEntity.setObj(incident);
    incidentEntity.setLastUpdate(incident.updatedAt)
    if (incidentEntity.getStatus() !== incident.status) {
      incidentEntity.setStatus(incident.status);
    }
    incidentEntity.setDescription(incident.description);
    incidentEntity.setName(incident.label);

    // Update geometries using the new efficient methods
    incidentEntity.updateHighGeometries(incident.highGeometries);
    incidentEntity.updateMedGeometries(incident.mediumGeometries);
    incidentEntity.updateLowGeometries(incident.lowGeometries);

    if (incident.affectedPerimeters?.length > 0) {
      incidentEntity.setAffectedPerimeterIds(incident.affectedPerimeters);
    }
    if (incident.focalPoint && !incidentEntity.focalPoint) {
      incidentEntity.setFocalPoint(incident.focalPoint);
    }
    return incidentEntity;
  }

  parseIncidentUpdate(update: any, incident: any ,playback: boolean): any {
    let updateEntity;
    if (update.geometry) {
      updateEntity = new UpdateMapEntity(update, incident.id, playback, this.cameraService, this.coreService,this.windowService,this.dataContextService,this.modalService,this.timelineService);
    }
    return updateEntity;
  }

  updateIncidentUpdate(update: any, updateEntity: any): any {
    if (updateEntity.getLastUpdate() !== update.updatedAt) {
      updateEntity.update(update);
    }
    return updateEntity;
  }

  parseIncidentSafezone(safezone: any, incident: any, playback: boolean): any {
    const safeZoneEntity = new SafeZoneMapEntity(safezone, incident.id, playback ,this.cameraService,this.cores,this.coreService,this.windowService,this.dataContextService,this.modalService,this.timelineService);
    if (safeZoneEntity) {
      safeZoneEntity.setLastUpdate(safezone.updatedAt);
    }
    return safeZoneEntity;
  }
  
  updateIncidentSafezone(safezone: any, safezoneEntity: any): any {
    if (safezoneEntity.getLastUpdate() !== safezone.updatedAt) {
      safezoneEntity.setLastUpdate(safezone.updatedAt);
      safezoneEntity.setDescription(safezone.description);
      safezoneEntity.setName(safezone.label);
      let lat;
      let lon;
      if (safezone.geometry && safezone.geometry.type === 'Point') {
        lat = safezone.geometry.coordinates[1];
        lon = safezone.geometry.coordinates[0];
      } else if (safezone.geometry && safezone.geometry.type === 'Polygon') {
        const polygon = { type: 'Feature', geometry: safezone.geometry };
        const centroid = turf.centroid(polygon as any);
        if (centroid) {
          lat = centroid.geometry.coordinates[1];
          lon = centroid.geometry.coordinates[0];
        }
      }
      if (lat && lon) {
        safezoneEntity?.setLatitude(lat);
        safezoneEntity?.setLongitude(lon);
      }
      safezoneEntity?.setIsActive(safezone.isActive);
    }
    return safezoneEntity;
  }

  reverseCoordinates(coordinates: any[]): any[] {
    const newCoordinates = [];
    for (let c = 0; c < (coordinates?.length ?? 0); c++) {
      const point = coordinates[c];
      let lon: any;
      let lat: any;
      if (Array.isArray(point) && point.length >= 2) {
        lon = point[0];
        lat = point[1];
      } else if (point && typeof point === 'object') {
        // Common key variants
        lon = point.lon ?? point.lng ?? point.longitude ?? point[0];
        lat = point.lat ?? point.latitude ?? point[1];
      }

      const nLat = typeof lat === 'string' ? Number(lat) : lat;
      const nLon = typeof lon === 'string' ? Number(lon) : lon;
      if (typeof nLat === 'number' && typeof nLon === 'number' && !Number.isNaN(nLat) && !Number.isNaN(nLon)) {
        newCoordinates.push([nLat, nLon]);
      }
    }
    return newCoordinates;
  }

  flipLocations(locations: any[]): any[] {
    const flippedLocations: any[][] = [];
    for (let i = 0; i < locations.length; i++) {
      flippedLocations[i] = [];
      flippedLocations[i][0] = locations[i][1];
      flippedLocations[i][1] = locations[i][0];
      if (locations[i][2] !== undefined) {
        flippedLocations[i][2] = locations[i][2];
      }
    }
    return flippedLocations;
  }

  flipLocationsNoZ(locations: any[]): any[] {
    const flippedLocations: any[][] = [];
    for (let i = 0; i < locations.length; i++) {
      flippedLocations[i] = [];
      flippedLocations[i][0] = locations[i][1];
      flippedLocations[i][1] = locations[i][0];
    }
    return flippedLocations;
  }

  clearProcall(): void {
    // TODO: implement if needed
  }

  clearIncident(incidentMapEntity: any): void {
    // this.socialMediaService.removeIncidentSocialMedia(incidentMapEntity.getId());
  }

  checkForPointsInPolygon(postData: any): any {
    // return this.http.post(this.environment.config.perimeters + '/globals/inside', postData);
  }

  parseCalendarEvent(calendarEvent: any): any {
    const value = calendarEvent;
    if (calendarEvent.notes && Array.isArray(calendarEvent.notes)) {
      calendarEvent.notes.forEach((note: any) => {
        note.formattedDate = moment(note.createdAt).format('MMM DD hh:mm:ss');
      });
    }
    return value;
  }

  parseActiveTrackingEvent(activeTrackingEvent: any, playback: boolean): any {
    const value = activeTrackingEvent;
    if (!playback) {
      if (moment() > moment(value.checkInTime)) {
        value.missed = true;
      }
      if (value.panicButtonClicked) {
        value.indistress = true;
      }
    }
    if (activeTrackingEvent.notes && Array.isArray(activeTrackingEvent.notes)) {
      activeTrackingEvent.notes.forEach((note: any) => {
        note.formattedDate = moment(note.createdAt).format('MMM DD hh:mm:ss');
      });
    }
    return value;
  }


  getPerimetersApiUrl(): string {
    return '' ;//this.environment.config.perimeters;
  }
  getMainApiUrl(): string {
    return '' ;//this.environment.config.arcangel;
  }
  getApiToken(): string {
    return '' ;// this.authentication.user.key;
  }

  latLonToLocation(lat: number, lon: number): Promise<string> {
    const url = ConfigurationManagerService.settings.tezzeractAPi + 'misc/reverseGeocode?lat=' + lat + '&lon=' + lon;
    return this.http.get<any>(url).toPromise().then((response) => {
      const json = response.data.address;
      let location = '';
      if (json) {
        if (json.streetNumber) {
          location = location + json.streetNumber + ' ';
        }
        if (json.street) {
          location = location + json.street + ' ';
        }
        if (json.adminCode1) {
          location = location + json.adminCode1 + ' ';
        }
        if (json.countryCode) {
          location = location + json.countryCode + ' ';
        }
      }
      return location;
    }).catch((error) => {
      // $log.error('Error retrieving reverse geocode.', error);
      return 'Unknown';
    });
  }

  latLonToLocationGmap(lat: number, lon: number): Promise<string> {
    const url = ConfigurationManagerService.settings.tezzeractAPi + 'misc/reverseGeocodeGmap?lat=' + lat + '&lon=' + lon;
    return this.http.get<any>(url).toPromise().then((response) => {
      let location = 'Unknown';
      if (response.results && response.results.length > 0) {
        location = response.results[0].formatted_address;
      }
      return location;
    }).catch((error) => {
      // $log.error('Error retrieving reverse geocode.', error);
      return 'Unknown';
    });
  }
}